import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { getClientServiceLocations, searchClients } from '@/features/clients/api/clients';
import {
  getLocationReports,
  type ServiceReport,
} from '@/features/service-locations/api/serviceLocations';

interface ReportRow {
  clientId: number;
  clientName: string;
  locationId: number;
  locationName: string;
  report: ServiceReport;
}
async function loadReports(): Promise<ReportRow[]> {
  const clients = await searchClients();
  const locations = (
    await Promise.all(
      clients.map(async (client) =>
        (await getClientServiceLocations(client.id)).map((location) => ({ client, location })),
      ),
    )
  ).flat();
  const rows = await Promise.all(
    locations.map(async ({ client, location }) =>
      (await getLocationReports(client.id, location.id)).map((report) => ({
        clientId: client.id,
        clientName: client.name,
        locationId: location.id,
        locationName: location.name,
        report,
      })),
    ),
  );
  return rows.flat().sort((a, b) => b.report.created_at.localeCompare(a.report.created_at));
}
export function ReportsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const query = useQuery({ queryKey: ['all-service-reports'], queryFn: loadReports });
  const rows = (query.data ?? []).filter((x) =>
    `${x.clientName} ${x.locationName} ${x.report.user?.name ?? ''}`
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minHeight: 44, px: 1 }}>
          <IconButton size="small" aria-label="Back" onClick={() => history.back()}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void query.refetch()}
            sx={commandButtonSx}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>
      {query.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Service reports could not be loaded.
        </Alert>
      )}
      <Paper elevation={0} sx={{ ...paperSx, mt: 1, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={1.5}
          sx={{ minHeight: 58, px: 2, py: 1 }}
        >
          <Typography component="h1" variant="h1" sx={{ mr: 'auto' }}>
            Service Reports
          </Typography>
          <Tooltip title="Filter reports">
            <FilterAltOutlinedIcon sx={{ color: 'text.disabled' }} />
          </Tooltip>
          <TextField
            size="small"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter by keyword"
            aria-label="Filter service reports by keyword"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { height: 36 } }}
          />
        </Stack>
        <Divider />
        <Box sx={{ p: 1.5, overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '150px minmax(180px,1fr) minmax(180px,1fr) 170px 130px',
            minWidth: 760,
          }}
        >
          {['Date', 'Client', 'Service location', 'Technician', 'Status'].map((x) => (
            <Typography key={x} fontWeight={600} sx={cellSx}>
              {x}
            </Typography>
          ))}
          {rows.map((x) => {
            const reportUrl = `/clients/${x.clientId}/service-locations/${x.locationId}/reports/${x.report.id}`;
            return (
            <Box
              key={`${x.locationId}-${x.report.id}`}
              role="link"
              tabIndex={0}
              aria-label={`Open service report for ${x.clientName} at ${x.locationName}`}
              onClick={() => void navigate(reportUrl)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  void navigate(reportUrl);
                }
              }}
              sx={{
                display: 'grid',
                gridColumn: '1 / -1',
                gridTemplateColumns: 'subgrid',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f3f2f1' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
              }}
            >
              <Typography sx={cellSx}>
                {new Date(x.report.created_at).toLocaleDateString('en-GB')}
              </Typography>
              <Typography sx={cellSx}>
                <Button
                  component={Link}
                  to={`/clients/${x.clientId}`}
                  onClick={(event) => event.stopPropagation()}
                  sx={{ p: 0, textTransform: 'none' }}
                >
                  {x.clientName}
                </Button>
              </Typography>
              <Typography sx={cellSx}>
                <Button
                  component={Link}
                  to={`/clients/${x.clientId}/service-locations/${x.locationId}`}
                  onClick={(event) => event.stopPropagation()}
                  sx={{ p: 0, textTransform: 'none' }}
                >
                  {x.locationName}
                </Button>
              </Typography>
              <Typography sx={cellSx}>{x.report.user?.name ?? '—'}</Typography>
              <Box sx={cellSx}>
                <Chip
                  size="small"
                  label={x.report.completed_at ? 'Completed' : 'In progress'}
                  color={x.report.completed_at ? 'success' : 'warning'}
                />
              </Box>
            </Box>
            );
          })}
        </Box>
        {!query.isLoading && !rows.length && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No matching service reports.
          </Typography>
        )}
        <Typography variant="body2" sx={{ pt: 1.5 }}>
          Rows: {rows.length}
        </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const cellSx = {
  minHeight: 48,
  p: 1.25,
  borderBottom: '1px solid #edebe9',
  display: 'flex',
  alignItems: 'center',
};
